import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatNumber } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Eye, Users, MousePointerClick, Percent, Globe, MapPin, Package, Store,
  RefreshCw, Activity,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from "recharts";

const RANGES = [
  { value: "7", label: "7 hari terakhir" },
  { value: "30", label: "30 hari terakhir" },
  { value: "90", label: "90 hari terakhir" },
  { value: "0", label: "Semua waktu" },
];

const BAR_COLORS = [
  "hsl(182 58% 20%)", "hsl(160 55% 26%)", "hsl(39 46% 45%)", "hsl(182 45% 32%)",
  "hsl(200 50% 34%)", "hsl(38 70% 40%)", "hsl(220 40% 40%)", "hsl(160 40% 38%)",
];

function shortDay(iso) {
  if (!iso || iso.length < 10) return iso || "";
  const [, m, d] = iso.split("-");
  return `${parseInt(d, 10)}/${parseInt(m, 10)}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "baru saja";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} mnt lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  const d = Math.floor(h / 24);
  return `${d} hari lalu`;
}

function Kpi({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const toneCls = {
    default: "text-primary bg-primary/10",
    success: "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))]",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))]",
    accent: "text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent))]/15",
  }[tone];
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${toneCls}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-heading text-2xl font-semibold tracking-tight mt-0.5 tabular-nums" data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children, right }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <p className="font-heading font-medium">{title}</p>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const { selectedTripId, trips } = useApp();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const tripName = selectedTripId && selectedTripId !== "all"
    ? (trips.find((t) => t.id === selectedTripId)?.name || "Katalog terpilih")
    : "Semua katalog";

  const load = useCallback(async (isRefresh) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get("/analytics/summary", {
        params: { trip_id: selectedTripId || "all", days: Number(days) },
      });
      setData(r.data);
    } catch (e) {
      console.error("Gagal memuat analitik:", e);
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedTripId, days]);

  useEffect(() => { load(false); }, [load]);

  if (loading) return <Loading label="Memuat analitik katalog..." />;

  const totals = data?.totals || { views: 0, unique_visitors: 0, clicks: 0, conversion: 0, events: 0 };
  const timeline = data?.timeline || [];
  const topProducts = data?.top_products || [];
  const locations = data?.locations || [];
  const topCities = data?.top_cities || [];
  const catalogues = data?.catalogues || [];
  const recent = data?.recent || [];
  const hasAnyData = totals.events > 0;
  const maxLocation = Math.max(1, ...locations.map((l) => l.visitors));

  return (
    <div data-testid="analytics-page">
      <PageHeader
        title="Analitik Katalog"
        subtitle={`Lacak kunjungan & klik produk · ${tripName}`}
      >
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="analytics-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value} data-testid={`analytics-range-${r.value}`}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="analytics-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />Perbarui
        </Button>
      </PageHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi icon={Eye} label="Katalog Dibuka" value={formatNumber(totals.views)} sub="Total tampilan halaman" testId="kpi-views" />
        <Kpi icon={Users} label="Pengunjung Unik" value={formatNumber(totals.unique_visitors)} sub="Berdasarkan perangkat" tone="success" testId="kpi-unique" />
        <Kpi icon={MousePointerClick} label="Klik Produk" value={formatNumber(totals.clicks)} sub="Produk dibuka" tone="accent" testId="kpi-clicks" />
        <Kpi icon={Percent} label="Rasio Klik" value={`${totals.conversion}%`} sub="Klik per tampilan" tone="warning" testId="kpi-conversion" />
      </div>

      {!hasAnyData ? (
        <div className="mt-5 rounded-xl border border-border bg-card shadow-sm">
          <EmptyState
            title="Belum ada aktivitas"
            subtitle="Data akan muncul saat pelanggan membuka tautan katalog atau melihat produk. Bagikan link katalog untuk mulai melacak."
          />
        </div>
      ) : (
        <>
          {/* Timeline */}
          <div className="mt-5">
            <ChartCard title="Tren Kunjungan">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={timeline} margin={{ left: -12, right: 6, top: 6 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(182 58% 20%)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(182 58% 20%)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(39 46% 45%)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(39 46% 45%)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11 }} minTickGap={20} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={34} />
                  <Tooltip
                    labelFormatter={(l) => shortDay(l)}
                    contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="views" name="Kunjungan" stroke="hsl(182 58% 20%)" strokeWidth={2} fill="url(#gViews)" />
                  <Area type="monotone" dataKey="clicks" name="Klik produk" stroke="hsl(39 46% 45%)" strokeWidth={2} fill="url(#gClicks)" />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "hsl(182 58% 20%)" }} />Kunjungan</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "hsl(39 46% 45%)" }} />Klik produk</span>
              </div>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {/* Top products */}
            <ChartCard title="Produk Paling Diklik">
              {topProducts.length ? (
                <div className="space-y-3" data-testid="analytics-top-products">
                  {topProducts.slice(0, 8).map((p, i) => {
                    const max = topProducts[0]?.clicks || 1;
                    const pct = Math.round((p.clicks / max) * 100);
                    return (
                      <div key={p.product_id} className="flex items-center gap-3" data-testid="analytics-top-product-row">
                        <span className="h-6 w-6 shrink-0 rounded-md bg-secondary text-[11px] font-semibold flex items-center justify-center text-muted-foreground">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium truncate">{p.product_name}</p>
                            <span className="text-sm font-semibold tabular-nums shrink-0">{formatNumber(p.clicks)}</span>
                          </div>
                          <div className="mt-1.5 h-2 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full bg-primary/80" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-10 text-center">Belum ada klik produk</p>
              )}
            </ChartCard>

            {/* Locations */}
            <ChartCard title="Lokasi Pengunjung">
              {locations.length ? (
                <div className="space-y-4">
                  <div className="space-y-2.5" data-testid="analytics-locations">
                    {locations.slice(0, 6).map((l) => (
                      <div key={l.country} className="flex items-center gap-3" data-testid="analytics-location-row">
                        <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium truncate">{l.country}</p>
                            <span className="text-sm tabular-nums text-muted-foreground shrink-0">{formatNumber(l.visitors)} pengunjung</span>
                          </div>
                          <div className="mt-1.5 h-2 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full bg-[hsl(var(--success))]/70" style={{ width: `${Math.round((l.visitors / maxLocation) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {topCities.length > 0 && (
                    <div className="border-t border-border pt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Kota teratas</p>
                      <div className="flex flex-wrap gap-2">
                        {topCities.slice(0, 8).map((c) => (
                          <span key={c.city} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-2.5 py-1 text-xs" data-testid="analytics-city-chip">
                            {c.city}<span className="text-muted-foreground tabular-nums">{c.visitors}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-10 text-center">Lokasi belum terdeteksi</p>
              )}
            </ChartCard>
          </div>

          {/* Per-catalogue breakdown (mostly useful when viewing all trips) */}
          {catalogues.length > 1 && (
            <div className="mt-4">
              <ChartCard title="Kunjungan per Katalog">
                <ResponsiveContainer width="100%" height={Math.max(180, catalogues.length * 44)}>
                  <BarChart data={catalogues} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="trip_name" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    <Bar dataKey="views" name="Kunjungan" radius={[0, 6, 6, 0]}>
                      {catalogues.map((c, i) => <Cell key={c.trip_id} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}

          {/* Recent activity */}
          <div className="mt-4">
            <ChartCard title="Aktivitas Terbaru" right={<Activity className="h-4 w-4 text-muted-foreground" />}>
              {recent.length ? (
                <div className="divide-y divide-border" data-testid="analytics-recent">
                  {recent.map((e, i) => {
                    const isClick = e.event_type === "product_click";
                    const Icon = isClick ? MousePointerClick : Eye;
                    return (
                      <div key={i} className="flex items-center gap-3 py-2.5" data-testid="analytics-recent-row">
                        <span className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${isClick ? "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent-foreground))]" : "bg-primary/10 text-primary"}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm truncate">
                            {isClick ? <>Klik produk <span className="font-medium">{e.product_name || "-"}</span></> : "Katalog dibuka"}
                            {e.trip_name && <span className="text-muted-foreground"> · {e.trip_name}</span>}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <MapPin className="h-3 w-3" />{e.location}
                          </p>
                        </div>
                        <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(e.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-10 text-center">Belum ada aktivitas</p>
              )}
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}
